/**
 * Ticket Codes — the editable master behind Ticket Station's code dropdown.
 *
 * Jay asked for exactly this (email 2026-08-11, item 9): "keep the phase list in a config table
 * rather than hardcoding it, because these codes will change over time and I want to be able to
 * edit them without asking you." So the 76 RACI codes live in 00_PORTAL_task_types and are edited
 * here, by whoever holds "Manage ticket codes".
 *
 * Deactivating rather than deleting is deliberate: tickets already raised store their code, and a
 * deleted row would leave those tickets pointing at nothing. An inactive code simply stops being
 * offered on the New Ticket form.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, Pencil, Plus, Search } from 'lucide-react';

import { BACKEND_URL } from './backendConfig';
import portalI18n from './i18n';
import useEscapeClose from './useEscapeClose';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import ListPicker from './ListPicker';
// ListPicker reuses the tracker's Partner / Line styling, which lives in that stylesheet.
import './SampleTracker.css';
import './TicketCodes.css';
import { pickL10n, langOf } from './l10nField';

const API = `${BACKEND_URL}/api/development/project-tracker/task-types`;

// One column per field; `lang` swaps in the reader's language stored beside the English (pickL10n).
// The HEADINGS come from the dictionary (`t`, all four languages) — the same words as the form's own
// labels (tc.f*), so the column and the field it edits are named alike. The getters are untouched:
// what they return is the data, and the Active column's Yes/No is also its filter value.
const buildColumns = (lang, t) => [
  { key: 'code', label: t('tc.fCode'), get: (r) => r.code, width: 96 },
  { key: 'name', label: t('tc.fName'), get: (r) => pickL10n(r, 'name', lang), width: 340 },
  { key: 'requestingTeam', label: t('tc.fTeam'), get: (r) => r.requestingTeam || r.teamKey, width: 150 },
  { key: 'categoryScope', label: t('tc.fCategory'), get: (r) => String(r.categoryScope || '').split(';').join(' / '), width: 190 },
  { key: 'caseName', label: t('tc.fCase'), get: (r) => r.caseName, width: 66 },
  { key: 'phase', label: t('tc.fPhase'), get: (r) => pickL10n(r, 'phase', lang), width: 210 },
  { key: 'receivingTeam', label: t('tc.fAssignedTo'), get: (r) => pickL10n(r, 'receivingTeam', lang), width: 180 },
  { key: 'groupEmail', label: t('tc.fGroupEmail'), get: (r) => r.groupEmail, width: 260 },
  { key: 'isActive', label: t('tc.stateActive'), get: (r) => (r.isActive ? 'Yes' : 'No'), width: 74 },
];
// Only its length and widths are read at module level, so the labels here never reach the screen.
const COLUMNS = buildColumns('en', (k) => k);
const splitCats = (v) => String(v || '').split(';').map((x) => x.trim()).filter(Boolean);

function CategoryPicker({ options, value, onChange }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const selected = splitCats(value);
  const summary = !selected.length ? t('tc.everyCategory')
    : selected.length <= 2 ? selected.join(' / ')
      : `${selected.slice(0, 2).join(' / ')} +${selected.length - 2}`;
  useEffect(() => {
    const close = (e) => { if (ref.current?.open && !ref.current.contains(e.target)) ref.current.removeAttribute('open'); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const toggle = (c) => {
    const next = selected.includes(c) ? selected.filter((x) => x !== c) : [...selected, c];
    onChange(joinCats(next));
  };
  return (
    <details className="tc-catdd" ref={ref}>
      <summary title={summary}>{summary}</summary>
      <div className="tc-catdd-panel">
        <label className="tc-catdd-row">
          <input type="checkbox" checked={!selected.length} onChange={() => onChange('')} />
          {t('tc.everyCategory')}
        </label>
        {options.map((c) => (
          <label className="tc-catdd-row" key={c}>
            <input type="checkbox" checked={selected.includes(c)} onChange={() => toggle(c)} />
            {c}
          </label>
        ))}
      </div>
    </details>
  );
}
const joinCats = (list) => list.join(';');
// Excel has room the table does not, so the export carries Note as an extra last column.
// Its label is filled in at export time, in the reader's language (tc.fNote).
const EXPORT_EXTRA = { key: 'note', label: 'Note', get: (r) => r.note, width: 320 };   // Excel only
// Action holds View and Edit since 2026-10-16 — measured after the build, not guessed.
const ACTION_W = 170;
const GRID_W = COLUMNS.reduce((sum, c) => sum + c.width, 0) + 44 + ACTION_W;   // + No. + Action

const emptyCode = () => ({
  code: '', name: '', nameKo: '', teamKey: 'T2', categoryScope: '', caseName: '', phase: '', phaseKo: '',
  phaseOrder: 0, requestingTeam: 'Sales OP', receivingTeam: '', receivingTeamKo: '',
  groupEmail: '', note: '', isActive: true,
});

const api = async (path = '', options = {}) => {
  const res = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || portalI18n.t('xTicket.requestFailed', { status: res.status }));
  return data;
};

export default function TicketCodes() {
  const { t, i18n } = useTranslation();
  // Name, phase and receiving team in the reader's language where the code carries it (Korean since the codes
  // were seeded, Vietnamese and Chinese since 2026-10-21); the admin form still edits English and Korean.
  const lang = langOf(i18n.language);
  const columns = useMemo(() => buildColumns(lang, t), [lang, t]);
  const getters = useMemo(() => Object.fromEntries(columns.map((c) => [c.key, c.get])), [columns]);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ phases: [], cases: [], receivingTeams: [], salesOpTeams: [], projectCategories: [], canEdit: false });
  const [extraTeams, setExtraTeams] = useState([]);   // added this session, not yet saved on any code
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editErr, setEditErr] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [editing, setEditing] = useState(null);   // { mode: 'new' | 'edit', form }
  const [saving, setSaving] = useState(false);

  // Esc = the same as the × button, except mid-save.
  useEscapeClose(() => setEditing(null), !!editing && !saving);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api();
      setRows(data.taskTypes || []);
      setMeta({
        phases: data.phases || [], cases: data.cases || [],
        receivingTeams: data.receivingTeams || [], salesOpTeams: data.salesOpTeams || [],
        projectCategories: data.projectCategories || [], canEdit: !!data.canEdit,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!message && !error) return undefined;
    const id = setTimeout(() => { setMessage(''); setError(''); }, 15000);
    return () => clearTimeout(id);
  }, [message, error]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = rows.filter((r) => (showInactive || r.isActive)
      && (!q || [r.code, r.name, r.nameKo, r.nameVi, r.nameZh, r.phase, r.receivingTeam, r.groupEmail, r.categoryScope]
        .some((v) => String(v || '').toLowerCase().includes(q))));
    const filtered = Object.keys(colFilters).length
      ? base.filter((r) => rowPassesColumnFilters(r, colFilters, getters))
      : base;
    return sortRows(filtered, sort, getters);
  }, [rows, search, showInactive, colFilters, sort, getters]);

  const thProps = (key) => ({
    sortKey: key,
    sort,
    onSort: (k) => setSort((p) => (p.key === k ? { key: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'asc' })),
    filterValues: openFilter?.key === key ? distinctColumnValues(rows, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter((p) => (p && p.key === k ? null : { key: k, anchor })),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, values, allValues) => {
      setColFilters((prev) => {
        const next = { ...prev };
        if (!values || !values.length || values.length === allValues.length) delete next[k];
        else next[k] = values;
        return next;
      });
      setOpenFilter(null);
    },
  });

  const save = async () => {
    setSaving(true);
    setEditErr('');
    try {
      const { form, mode } = editing;
      if (mode === 'new') await api('', { method: 'POST', body: JSON.stringify(form) });
      else await api(`/${form.id}`, { method: 'PUT', body: JSON.stringify(form) });
      setMessage(mode === 'new' ? t('tc.created', { code: form.code }) : t('tc.saved', { code: form.code }));
      setEditing(null);
      await load();
    } catch (e) {
      setEditErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const setField = (k) => (e) => setEditing((p) => ({ ...p, form: { ...p.form, [k]: e.target.value } }));

  // Teams offered for Receiving Team: the ones already in use, plus any added during this session,
  // plus whatever the row being edited already holds (so an old value is never silently dropped).
  const teamOptions = useMemo(() => {
    const all = [...(meta.receivingTeams || []), ...extraTeams];
    const cur = editing?.form?.receivingTeam;
    if (cur && !all.includes(cur)) all.push(cur);
    return [...new Set(all)].sort();
  }, [meta.receivingTeams, extraTeams, editing]);

  const exportXlsx = async () => {
    if (!visible.length) { setMessage(t('tc.nothingToExport')); return; }
    const cols = [...columns, { ...EXPORT_EXTRA, label: t('tc.fNote') }];
    const filters = [showInactive ? t('tc.exportInclInactive') : t('tc.exportActiveOnly')];
    if (search.trim()) filters.push(t('xTicket.codesSearchFilter', { q: search.trim() }));
    Object.entries(colFilters).forEach(([key, values]) => {
      const label = (columns.find((c) => c.key === key) || {}).label || key;
      filters.push(`${label}: ${[...values].join(', ')}`);
    });
    const stamp = new Date().toISOString().slice(0, 16).replace('T', ' ');
    try {
      setMessage(t('tc.preparingExport'));
      const res = await fetch(`${BACKEND_URL}/api/development/sample-tracker/export-xlsx`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: t('xTicket.codesExportTitle', { title: t('tc.title') }),
          sheetName: t('tc.title'),
          generated: stamp,
          filters,
          columns: ['#', ...cols.map((c) => c.label)],
          // px on screen / 7 is about one Excel character, and the clamp stops Note from swallowing the sheet.
          widths: [6, ...cols.map((c) => Math.min(46, Math.max(9, Math.round(c.width / 7))))],
          statusColumn: 0,          // no status column here — skip the colour coding
          freezeColumns: 3,         // #, Code, Ticket Name stay visible
          rows: visible.map((r, i) => ({
            type: 'base',
            values: [i + 1, ...cols.map((c) => { const v = c.get(r); return v === '' || v === null || v === undefined ? '-' : String(v); })],
          })),
          filename: `HEVN_Ticket_Codes_${stamp.replace(/[: ]/g, '-')}.xlsx`,
        }),
      });
      if (!res.ok) {
        let msg = t('xTicket.exportFailedStatus', { status: res.status });
        try { const j = await res.json(); if (j.error) msg = j.error; } catch (e) { /* non-JSON body */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const match = (res.headers.get('Content-Disposition') || '').match(/filename="?([^"]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : 'HEVN_Ticket_Codes.xlsx';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
      setMessage(t('tc.exported', { n: visible.length }));
    } catch (err) {
      setError(err.message || t('tc.exportFailed'));
    }
  };

  return (
    <div className="tc-panel">
      <div className="tc-head">
        <div>
          <h3>{t('tc.title')}</h3>
          <p>
            {t('tc.subtitle', { n: rows.length })}
            {meta.canEdit ? '' : ` ${t('tc.readOnly')}`}
          </p>
        </div>
        <div className="tc-head-actions">
          {meta.canEdit && (
            <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditErr(''); setEditing({ mode: 'new', form: emptyCode() }); }}>
              <Plus size={14} /> {t('tc.newCode')}
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={exportXlsx} disabled={!visible.length}>
            <Download size={14} /> {t('tc.exportExcel')}
          </button>
        </div>
      </div>

      {error && <div className="tc-banner err">{error}</div>}
      {message && <div className="tc-banner ok">{message}</div>}

      {/* No Request forms tab: Max, 2026-09-02 — he and I set the forms up, and if Jay wants one
          changed he says so. A screen nobody opens is a screen that goes stale. The API and
          scripts/ticket_forms_seed.cjs are still there, so re-adding it is minutes if that changes. */}
      <div className="tc-bar">
        <span className="tc-search">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('tc.searchPh')} />
        </span>
        <label className="tc-check">
          <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
          {t('tc.showInactive')}
        </label>
        {(!!sort.key || !!Object.keys(colFilters).length) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSort({ key: '', dir: 'asc' }); setColFilters({}); }}>
            {t('tc.clearTools')}
          </button>
        )}
        <span className="tc-count">{t('tc.countOf', { n: visible.length, total: rows.length })}</span>
      </div>

      {loading && <div className="tc-empty">{t('tc.loading')}</div>}
      {!loading && (
        <div className="tc-scroll">
          {/* Frozen identity columns + Action: the row is 15 columns wide, and without them you lose
              track of which code you are reading. */}
          <table className="tc-table tc-sticky" style={{ width: GRID_W, minWidth: GRID_W }}>
            <colgroup>
              <col style={{ width: 44 }} />
              {columns.map((c) => <col key={c.key} style={{ width: c.width }} />)}
              <col style={{ width: ACTION_W }} />
            </colgroup>
            <thead>
              <tr>
                <th className="tc-no">{t('tc.colNo')}</th>
                {columns.map((c, i) => (
                  <SortTh key={c.key} label={c.label} className={i < 2 ? 'tc-freeze' : ''} {...thProps(c.key)} />
                ))}
                <th className="tc-act">{t('tc.colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => (
                <tr key={r.id} className={r.isActive ? '' : 'off'}>
                  <td className="tc-no">{i + 1}</td>
                  {columns.map((c, ci) => {
                    if (c.key === 'isActive') {
                      return (
                        <td key={c.key}>
                          <span className={`tc-pill ${r.isActive ? 'on' : 'offp'}`}>{r.isActive ? t('tc.stateActive') : t('tc.stateInactive')}</span>
                        </td>
                      );
                    }
                    const cls = ci === 0 ? 'tc-code tc-freeze' : ci === 1 ? 'tc-name tc-freeze' : c.key === 'groupEmail' ? 'tc-mail' : '';
                    return <td key={c.key} className={cls}>{c.get(r) || '—'}</td>;
                  })}
                  <td className="tc-act">
                    {/* VIEW for everybody who can read the list — Jay, 2026-10-16 ("Same for all similar
                        function"); Edit stays with whoever may change codes. */}
                    <button type="button" className="btn btn-ghost btn-sm" title={t('tc.btnViewHint')}
                      onClick={() => { setEditErr(''); setEditing({ mode: 'view', form: { ...r } }); }}>
                      <Eye size={13} /> {t('tc.btnView')}
                    </button>
                    {meta.canEdit && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setEditErr(''); setEditing({ mode: 'edit', form: { ...r } }); }}>
                        <Pencil size={13} /> {t('tc.btnEdit')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!visible.length && <tr><td colSpan={COLUMNS.length + 2} className="tc-empty">{t('xTicket.codesNoMatch')}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="tc-overlay">
          <div className="tc-modal">
            <div className="tc-modal-head">
              <h3>{editing.mode === 'new' ? t('tc.formNew')
                : editing.mode === 'view' ? t('tc.formView', { code: editing.form.code })
                  : t('tc.formEdit', { code: editing.form.code })}
                {editing.mode === 'view' && <span className="tc-view-tag">{t('tc.viewOnly')}</span>}</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label={t('tc.btnClose')}>&times;</button>
            </div>
            <div className="tc-modal-body">
              <fieldset className="tc-ro" disabled={editing.mode === 'view'}>
              <div className="tc-grid">
                <label><span className="tc-lbl">{t('tc.fCode')} <span className="tc-req">*</span></span>
                  <input value={editing.form.code} onChange={setField('code')} placeholder="A-1" />
                </label>
                <label><span className="tc-lbl">{t('tc.fTeam')} <span className="tc-req">*</span></span>
                  {/* The Sales OP team that raises this ticket — the requesting team, in other words. */}
                  <select value={editing.form.teamKey} onChange={setField('teamKey')}>
                    {(meta.salesOpTeams || []).map((tm) => (
                      <option key={tm.teamKey} value={tm.teamKey}>{tm.teamName}</option>
                    ))}
                  </select>
                </label>
                <label>{t('tc.fCase')}
                  {/* Team 1 codes have no Case at all in v3 — blank is a valid answer, not a gap. */}
                  <input list="tc-cases" value={editing.form.caseName} onChange={setField('caseName')} placeholder={t('tc.phCase')} />
                </label>
                <label>{t('tc.fCategory')}
                  <CategoryPicker
                    options={meta.projectCategories || []}
                    value={editing.form.categoryScope}
                    onChange={(v) => setEditing((p) => ({ ...p, form: { ...p.form, categoryScope: v } }))}
                  />
                </label>
                <label className="wide"><span className="tc-lbl">{t('tc.fName')} <span className="tc-req">*</span></span>
                  <input value={editing.form.name} onChange={setField('name')} placeholder={t('tc.phName')} />
                </label>

                <label className="wide"><span className="tc-lbl">{t('tc.fPhase')} <span className="tc-req">*</span></span>
                  {/* The P-number decides where the phase sits in the New Ticket dropdown, so the
                      order is read from the name — there is nothing extra to fill in. */}
                  {/* free text with the existing phases as suggestions: Jay will add 2A / 2B and
                      later a different code set, and neither should need a deploy */}
                  <input list="tc-phases" value={editing.form.phase} onChange={setField('phase')} placeholder="P3 · Sample" />
                </label>


                <label>{t('tc.fAssignedTo')}
                  <ListPicker
                    value={editing.form.receivingTeam}
                    options={teamOptions}
                    onChange={(v) => {
                      setEditing((p) => ({ ...p, form: { ...p.form, receivingTeam: v } }));
                      if (v) setExtraTeams((prev) => (prev.includes(v) ? prev : [...prev, v]));
                    }}
                    placeholder={t('tc.phTeamSearch')}
                  />
                </label>

                <label className="wide">{t('tc.fGroupEmail')}
                  {/* Development and Production each notify two mailboxes, so this holds a list. */}
                  <input value={editing.form.groupEmail} onChange={setField('groupEmail')} placeholder="hevn-dev@helinox.com, hinc.design@helinox.co.kr" />
                </label>
                <label className="wide">{t('tc.fNote')}
                  <textarea rows={2} value={editing.form.note} onChange={setField('note')} />
                </label>
                <label className="tc-check wide">
                  <input
                    type="checkbox"
                    checked={!!editing.form.isActive}
                    onChange={(e) => setEditing((p) => ({ ...p, form: { ...p.form, isActive: e.target.checked } }))}
                  />
                  {t('tc.fActive')}
                </label>
              </div>
              </fieldset>
            </div>
            {editErr && <div className="tc-modal-err">{editErr}</div>}
            <div className="tc-modal-foot">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(null)} disabled={saving}>
                {editing.mode === 'view' ? t('tc.btnClose') : t('tc.btnCancel')}
              </button>
              {editing.mode !== 'view' && (
              <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                {saving ? t('tc.saving') : t('tc.btnSave')}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      <datalist id="tc-phases">{meta.phases.map((p) => <option key={p} value={p} />)}</datalist>
      <datalist id="tc-cases">{meta.cases.map((c) => <option key={c} value={c} />)}</datalist>
    </div>
  );
}
